import { ConfigurationObject } from '../services/configs-api';
import { getTemplate } from '../templates/registry';

/**
 * Exports a configuration's attributes as a CSV file.
 * Columns: Attribute Name, Label, Value, Type
 */
export function exportConfigAsCsv(config: ConfigurationObject): void {
  const template = getTemplate(config.configType);
  const schema = template?.payloadSchema ?? [];
  const schemaMap = new Map(schema.map((f) => [f.key, f]));

  const rows: string[][] = [['Attribute Name', 'Label', 'Value', 'Type']];

  // Schema-defined fields first
  for (const field of schema) {
    const value = config.payload[field.key];
    rows.push([
      field.key,
      field.label,
      formatValue(value),
      field.type,
    ]);
  }

  // Custom attributes (not in schema)
  for (const [key, value] of Object.entries(config.payload)) {
    if (!schemaMap.has(key)) {
      rows.push([key, key, formatValue(value), typeof value]);
    }
  }

  const csvContent = rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
  downloadFile(
    csvContent,
    `${config.configName}-attributes.csv`,
    'text/csv;charset=utf-8;'
  );
}

/**
 * Exports a configuration's attributes as a formatted PDF-like HTML document
 * that opens in a new tab for printing to PDF.
 */
export function exportConfigAsPdf(config: ConfigurationObject): void {
  const template = getTemplate(config.configType);
  const schema = template?.payloadSchema ?? [];
  const schemaMap = new Map(schema.map((f) => [f.key, f]));

  const displayName = template?.displayName ?? config.configType;

  // Group fields by section (using comment markers from the schema)
  const sections = groupFieldsBySection(schema, config.payload);

  // Custom attributes
  const customAttrs = Object.entries(config.payload).filter(([key]) => !schemaMap.has(key));

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${config.configName} — Configuration Export</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, sans-serif; color: #1a1a2e; padding: 40px; max-width: 900px; margin: 0 auto; }
    h1 { font-size: 24px; font-weight: 600; margin-bottom: 8px; color: #0B2545; }
    h2 { font-size: 16px; font-weight: 600; margin: 24px 0 12px; padding-bottom: 6px; border-bottom: 2px solid #0B2545; color: #0B2545; }
    .subtitle { font-size: 14px; color: #687078; margin-bottom: 24px; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 24px; padding: 16px; background: #f8f9fa; border-radius: 4px; }
    .meta-item label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #687078; }
    .meta-item span { font-size: 14px; display: block; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 13px; }
    th { text-align: left; padding: 8px 12px; background: #f1f3f5; border-bottom: 1px solid #dee2e6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
    td { padding: 8px 12px; border-bottom: 1px solid #f1f3f5; vertical-align: top; }
    td:first-child { font-weight: 500; width: 35%; }
    td:last-child { color: #495057; word-break: break-word; }
    .empty { color: #adb5bd; font-style: italic; }
    .bool-true { color: #2b8a3e; }
    .bool-false { color: #c92a2a; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #dee2e6; font-size: 11px; color: #adb5bd; }
    @media print { body { padding: 20px; } .no-print { display: none; } }
    .print-btn { position: fixed; top: 16px; right: 16px; padding: 8px 16px; background: #0B2545; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; }
    .print-btn:hover { background: #13315C; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Print / Save as PDF</button>
  <h1>${escapeHtml(config.configName)}</h1>
  <div class="subtitle">${escapeHtml(displayName)} Configuration — Attribute Export</div>
  <div class="meta">
    <div class="meta-item"><label>Config ID</label><span>${escapeHtml(config.configId)}</span></div>
    <div class="meta-item"><label>Type</label><span>${escapeHtml(displayName)}</span></div>
    <div class="meta-item"><label>Last Modified</label><span>${new Date(config.lastModified).toLocaleString()}</span></div>
    <div class="meta-item"><label>Version</label><span>${config.version}</span></div>
  </div>
  ${sections.map(({ title, fields }) => `
  <h2>${escapeHtml(title)}</h2>
  <table>
    <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
    <tbody>
      ${fields.map(({ label, value }) => `<tr><td>${escapeHtml(label)}</td><td>${formatHtmlValue(value)}</td></tr>`).join('')}
    </tbody>
  </table>`).join('')}
  ${customAttrs.length > 0 ? `
  <h2>Custom Attributes</h2>
  <table>
    <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
    <tbody>
      ${customAttrs.map(([key, value]) => `<tr><td>${escapeHtml(key)}</td><td>${formatHtmlValue(value)}</td></tr>`).join('')}
    </tbody>
  </table>` : ''}
  <div class="footer">
    Exported on ${new Date().toLocaleString()} • Deloitte Amazon Connect Portal
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Clean up after a delay to allow the new tab to load
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// --- Helpers ---

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatHtmlValue(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '<span class="empty">—</span>';
  }
  if (typeof value === 'boolean') {
    return value
      ? '<span class="bool-true">✓ Yes</span>'
      : '<span class="bool-false">✗ No</span>';
  }
  if (typeof value === 'object') {
    return escapeHtml(JSON.stringify(value, null, 2));
  }
  return escapeHtml(String(value));
}

function escapeCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface FieldRow {
  label: string;
  value: unknown;
}

interface Section {
  title: string;
  fields: FieldRow[];
}

function groupFieldsBySection(
  schema: { key: string; label: string }[],
  payload: Record<string, unknown>
): Section[] {
  if (schema.length === 0) {
    // No schema — just dump all payload fields
    const fields = Object.entries(payload).map(([key, value]) => ({ label: key, value }));
    return fields.length > 0 ? [{ title: 'Attributes', fields }] : [];
  }

  // Single section with all schema fields
  const fields: FieldRow[] = schema
    .map((field) => ({
      label: field.label,
      value: payload[field.key],
    }))
    .filter((f) => f.value !== undefined && f.value !== null && f.value !== '');

  return fields.length > 0 ? [{ title: 'Configuration Attributes', fields }] : [];
}
